import { useEffect, memo, useState, useRef, useCallback } from 'react';
import { Claim } from '@scale/shared/types/queue';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';

import { useClaimTime } from 'react-scale/hooks/useClaimTime';
import { usePauseCapability } from 'react-scale/components/scalers/tasks/common/PauseCapabilityHook';
import { useUnleashFeatureFlagWithLoading } from 'react-scale/hooks/useUnleashFeatureFlag';

import ClaimModal from './ClaimModal';
import { formatTimeHMS, formatTime, TIME_FORMAT_PRESETS } from '@scale/shared/utils/dateUtils';

type PauseModalProps = {
  claim?: Claim;
  taskId?: string;
  projectId?: string;
  assignment?: AssignmentJson;
};

/**
 * Calculate progress percentage from remaining and total time
 * @param remaining - The remaining time left
 * @param total - The total time available
 * @returns The progress percentage, between 0 and 100
 */
const calculateProgress = (remaining: number | null, total: number): number => {
  if (remaining === null || total <= 0) {
    return 0;
  }
  const progress = (remaining / total) * 100;
  return Math.max(0, Math.min(100, progress));
};

function PauseModal(props: PauseModalProps) {
  const { maxClaim: maxClaimSubmissionDeadlineDate } = useClaimTime(
    props.claim,
    props.projectId,
    props.assignment,
    props.taskId,
  );

  const [pausedDurationSeconds, setPausedDurationSeconds] = useState<number | null>(null);

  const [remainingSubmissionTimeSeconds, setRemainingSubmissionTimeSeconds] = useState<
    number | null
  >(null);
  const [remainingIdleTimeSeconds, setRemainingIdleTimeSeconds] = useState<number | null>(null);
  const [totalUsedPauseClaimExtensionSeconds, setTotalUsedPauseClaimExtensionSeconds] =
    useState<number>(0);

  const {
    isPaused,
    handlePauseClick,
    pausedAt,
    isLoading: isLoadingPause,
    pauseTimeClaimTimeExtensionInfo,
  } = usePauseCapability(props.taskId ?? '');

  const initialMaxClaimTimeSeconds = (props.claim?.claimMinutes || 0) * 60;

  // Pause claim time extension info
  const {
    data: pauseTimerClaimTimeExtensionFeatureEnabled,
    isLoading: isLoadingPauseTimerClaimTimeExtensionFeatureFlag,
  } = useUnleashFeatureFlagWithLoading('pause-timer-claim-time-extension', {}, false);

  const initialRemainingIdleTimeSeconds = pauseTimeClaimTimeExtensionInfo
    ? Math.max(
        0,
        pauseTimeClaimTimeExtensionInfo.maxAvailableSeconds -
          pauseTimeClaimTimeExtensionInfo.usedSeconds,
      )
    : 0;

  const hasPauseTimeClaimExtensionTimeAvailable =
    pauseTimerClaimTimeExtensionFeatureEnabled &&
    pauseTimeClaimTimeExtensionInfo &&
    pauseTimeClaimTimeExtensionInfo.maxAvailableSeconds > 0;

  const submittedPauseResume = useRef(false);

  // function to update the timers displayed
  const updateTimers = useCallback(() => {
    const now = new Date();
    const pausedAtDate = new Date(pausedAt);
    const pausedDurationMs = now.getTime() - pausedAtDate.getTime();
    const pausedDurationSecs = pausedDurationMs / 1000;

    if (!isLoadingPause && isPaused && !submittedPauseResume.current) {
      // Update paused duration display
      setPausedDurationSeconds(pausedDurationSecs);
    }

    // Calculate actual remaining time to deadline; same method as InfoDeadline.tsx
    const actualRemainingClaimTimeSeconds = Math.max(
      0,
      (new Date(maxClaimSubmissionDeadlineDate).getTime() - now.getTime()) / 1000,
    );

    // remaining pause time claim extension time (aka. 'idle time')
    const currentRemainingIdleTimeSeconds = Math.max(
      0,
      initialRemainingIdleTimeSeconds - pausedDurationSecs,
    );

    // Calculate idle time consumed during this pause session
    const currentPauseUsedIdleTimeSeconds =
      initialRemainingIdleTimeSeconds - currentRemainingIdleTimeSeconds;

    // total extension granted so far: previously used + consumed during this pause
    const totalUsedPauseClaimExtension =
      (pauseTimeClaimTimeExtensionInfo?.usedSeconds || 0) + currentPauseUsedIdleTimeSeconds;

    // Update state with total accumulated extension
    setTotalUsedPauseClaimExtensionSeconds(totalUsedPauseClaimExtension);

    let remainingSubmissionTimeSeconds_: number;

    if (hasPauseTimeClaimExtensionTimeAvailable) {
      setRemainingIdleTimeSeconds(currentRemainingIdleTimeSeconds);

      // calculate remaining submission time to display, with idle time compensation:
      // - actualRemainingClaimTimeSeconds: counts down naturally as time passes
      // - currentPauseUsedIdleTimeSeconds: counts up as remaining paused time is consumed, compensating the countdown
      // Result: submission time stays constant while idle time is available,
      //         then starts counting down once idle time is exhausted
      remainingSubmissionTimeSeconds_ =
        actualRemainingClaimTimeSeconds + currentPauseUsedIdleTimeSeconds;

      // when pause started with insufficient remaining claim time for the available pause idle time,
      // server pre-borrowed extension time and added it to maxClaim.
      // to compensate, we subtract it here to show the original deadline to the user during the pause session.
      remainingSubmissionTimeSeconds_ = Math.max(
        0,
        remainingSubmissionTimeSeconds_ -
          (pauseTimeClaimTimeExtensionInfo?.borrowedExtensionSeconds || 0),
      );
    } else {
      // Without pause time claim extension available: just use actual remaining submission time
      remainingSubmissionTimeSeconds_ = actualRemainingClaimTimeSeconds;
    }

    setRemainingSubmissionTimeSeconds(remainingSubmissionTimeSeconds_);
  }, [
    pausedAt,
    isLoadingPause,
    isPaused,
    maxClaimSubmissionDeadlineDate,
    initialRemainingIdleTimeSeconds,
    hasPauseTimeClaimExtensionTimeAvailable,
    pauseTimeClaimTimeExtensionInfo?.borrowedExtensionSeconds,
    pauseTimeClaimTimeExtensionInfo?.usedSeconds,
  ]);

  // run interval to update the timers displayed
  useEffect(() => {
    // execute immediately, then every few ms
    updateTimers();
    const interval = setInterval(updateTimers, 500);
    return () => clearInterval(interval);
  }, [updateTimers]);

  const deadlineStr = new Intl.DateTimeFormat('default', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(maxClaimSubmissionDeadlineDate);

  // handle pause resume button click
  const handlePauseResume = useCallback(() => {
    if (submittedPauseResume.current) {
      // already submitted, don't do it again
      return;
    }
    submittedPauseResume.current = true;
    handlePauseClick();
  }, [handlePauseClick]);

  // reset submittedPauseResume when pause state changes
  useEffect(() => {
    submittedPauseResume.current = false;
  }, [isPaused]);

  const renderTimer = ({
    title,
    remainingTimeSeconds,
    totalTimeSeconds,
  }: {
    title: string;
    remainingTimeSeconds: number | null;
    totalTimeSeconds: number;
  }) => {
    const remainingTimeString =
      remainingTimeSeconds === null || Number.isNaN(remainingTimeSeconds)
        ? 'Loading...'
        : formatTime(remainingTimeSeconds, TIME_FORMAT_PRESETS.REMAINING_TIME);
    const progressPercentage = calculateProgress(remainingTimeSeconds, totalTimeSeconds);

    return (
      <div
        className="flex-1 flex flex-col text-left gap-1 rounded-lg px-4 py-3 mx-2 min-w-[120px] max-w-[240px] border"
        style={{
          borderColor: 'var(--colors-mauve-7-light)',
          backgroundColor: 'inherit',
          maxWidth: '240px',
        }}
      >
        <div className="text-sm mb-2 font-bold" style={{ color: 'rgb(var(--surface-contrast))' }}>
          {title}
        </div>
        {/* Progress bar container */}
        <div
          className="w-full max-w-[120px] rounded-full h-2 mb-1 relative overflow-hidden"
          style={{
            // NOTE: --colors-mauve-alpha-3-light works, but should be ----colors-neutral-alpha-3---component-background-scale
            backgroundColor: 'var(--colors-mauve-alpha-3-light)',
            position: 'relative',
          }}
        >
          {/* filled progress bar */}
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-all duration-200"
            style={{
              width: `${progressPercentage}%`,
              // NOTE: --colors-purple-9-light doesn't work, also should be --colors-accent-9---solid-background-scale
              backgroundColor: 'var(--colors-scale-9-light)',
              position: 'absolute',
              zIndex: 1,
            }}
          />
        </div>
        {/* Time display */}
        <div className="text-gray-800">{remainingTimeString}</div>
      </div>
    );
  };

  const usedPausedTimeStr =
    totalUsedPauseClaimExtensionSeconds >= 60
      ? ` (extended by ${formatTime(
          totalUsedPauseClaimExtensionSeconds,
          TIME_FORMAT_PRESETS.TIME_SPENT_NO_SECONDS,
        )})`
      : '';

  if (!props.claim || !isPaused || isLoadingPauseTimerClaimTimeExtensionFeatureFlag) {
    return null;
  }

  return (
    <ClaimModal
      confirmButtonText="Resume Task"
      onConfirmButtonClick={handlePauseResume}
      isConfirmButtonLoading={isLoadingPause}
      title="Tasking Time Paused"
      timer={formatTimeHMS(pausedDurationSeconds)}
      subtitle={
        <div className="flex flex-col mt-5">
          <div className="flex flex-row justify-center mb-4">
            {/* Show only one timer: Idle Time if available, otherwise Submission Time */}
            {hasPauseTimeClaimExtensionTimeAvailable && (remainingIdleTimeSeconds ?? 0) > 0
              ? renderTimer({
                  title: 'Remaining Paused Time',
                  remainingTimeSeconds: remainingIdleTimeSeconds,
                  totalTimeSeconds: pauseTimeClaimTimeExtensionInfo.maxAvailableSeconds,
                })
              : renderTimer({
                  title: 'Remaining Submission Time',
                  remainingTimeSeconds: remainingSubmissionTimeSeconds,
                  totalTimeSeconds: initialMaxClaimTimeSeconds,
                })}
          </div>

          <div className="text-sm text-gray-600 mx-2 mt-2 mb-2">
            To receive payment, submit before the submission deadline on{' '}
            <span className="font-medium">{deadlineStr}</span>
            {usedPausedTimeStr}. Submissions will not be accepted after this time.
          </div>
        </div>
      }
    />
  );
}

export default memo(PauseModal);
